import { memo } from "react";
import { Box, Container, Typography, Button } from "@mui/material";
import { useNavigate } from "react-router-dom";
import { FullPaddingWrapper } from "../../Components/FullPaddingWrapper/FullPaddingWrapper";
import SecurityIcon from "@mui/icons-material/Security";

const DoNotSell = memo(() => {
  const navigate = useNavigate();
  return (
    <Container maxWidth="xl" disableGutters>
      <FullPaddingWrapper>
        {/* Hero Section */}
        <Box
          sx={{
            background: "linear-gradient(135deg, #004B4C 0%, #006668 100%)",
            borderRadius: { xs: "16px", md: "24px" },
            p: { xs: 4, sm: 6, md: 8 },
            mb: { xs: 4, md: 6 },
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", mb: 3 }}>
            <SecurityIcon
              sx={{ fontSize: { xs: 32, md: 48 }, color: "#ffffff", mr: 2 }}
            />
            <Typography
              variant="h2"
              sx={{
                color: "#ffffff",
                fontWeight: 700,
                fontSize: { xs: "1.5rem", sm: "2rem", md: "2.5rem" },
                lineHeight: 1.2,
              }}
            >
              Do Not Sell My Personal Information
            </Typography>
          </Box>
          <Typography
            variant="body1"
            sx={{
              color: "#ffffff",
              fontSize: { xs: "1rem", md: "1.1rem" },
              opacity: 0.9,
              maxWidth: 800,
            }}
          >
            Your privacy matters. Learn about your rights under the California
            Consumer Privacy Act (CCPA) and how to exercise them.
          </Typography>
        </Box>

        {/* Content Section */}
        <Box
          sx={{
            px: { xs: 2, md: 6, lg: 10 },
            py: { xs: 4, md: 6 },
            maxWidth: 1000,
            mx: "auto",
          }}
        >
          {/* Our Commitment */}
          <Box
            sx={{
              p: 4,
              borderRadius: "16px",
              backgroundColor: "#e8f5f5",
              border: "1px solid #004B4C20",
              mb: 6,
            }}
          >
            <Typography
              variant="h5"
              sx={{
                color: "primary.main",
                fontWeight: 700,
                mb: 2,
              }}
            >
              Our Commitment to You
            </Typography>
            <Typography
              variant="body1"
              sx={{ color: "#333", lineHeight: 1.8, fontSize: "1.1rem" }}
            >
              <strong>SimplerHome does not sell your personal information.</strong>{" "}
              We believe your data belongs to you, and we are committed to
              protecting your privacy. We only use your information to provide
              and improve our home management services.
            </Typography>
          </Box>

          {/* CCPA Rights */}
          <Box sx={{ mb: 6 }}>
            <Typography
              variant="h4"
              sx={{
                color: "primary.main",
                fontWeight: 700,
                fontSize: { xs: "1.5rem", md: "1.75rem" },
                mb: 4,
              }}
            >
              Your California Privacy Rights
            </Typography>

            <Typography
              variant="body1"
              sx={{ color: "#555", lineHeight: 1.8, mb: 4 }}
            >
              Under the California Consumer Privacy Act (CCPA), California
              residents have specific rights regarding their personal
              information:
            </Typography>

            <Box
              sx={{
                display: "grid",
                gap: 3,
              }}
            >
              {[
                {
                  title: "Right to Know",
                  description:
                    "You can request information about what personal data we collect, use, disclose, and sell about you.",
                },
                {
                  title: "Right to Delete",
                  description:
                    "You can request that we delete your personal information, subject to certain exceptions.",
                },
                {
                  title: "Right to Opt-Out",
                  description:
                    "You have the right to opt-out of the sale of your personal information. As stated, we do not sell your data.",
                },
                {
                  title: "Right to Non-Discrimination",
                  description:
                    "We will not discriminate against you for exercising any of your CCPA rights.",
                },
              ].map((right, index) => (
                <Box
                  key={index}
                  sx={{
                    p: 3,
                    borderRadius: "12px",
                    backgroundColor: "#f8f8f8",
                    borderLeft: "4px solid",
                    borderColor: "primary.main",
                  }}
                >
                  <Typography
                    variant="h6"
                    sx={{ color: "#333", fontWeight: 600, mb: 1 }}
                  >
                    {right.title}
                  </Typography>
                  <Typography
                    variant="body1"
                    sx={{ color: "#555", lineHeight: 1.6 }}
                  >
                    {right.description}
                  </Typography>
                </Box>
              ))}
            </Box>
          </Box>

          {/* Information We Collect */}
          <Box sx={{ mb: 6 }}>
            <Typography
              variant="h4"
              sx={{
                color: "primary.main",
                fontWeight: 700,
                fontSize: { xs: "1.5rem", md: "1.75rem" },
                mb: 4,
              }}
            >
              Categories of Information We Collect
            </Typography>

            <Box component="ul" sx={{ pl: 3 }}>
              {[
                "Identifiers (name, email address, account name)",
                "Contact information (address, phone number)",
                "Property information (home address, property details)",
                "Documents and records you upload",
                "Usage data and browsing history on our platform",
                "Device information and IP addresses",
              ].map((item, index) => (
                <Box
                  component="li"
                  key={index}
                  sx={{
                    color: "#555",
                    py: 1,
                    fontSize: "1rem",
                    lineHeight: 1.6,
                  }}
                >
                  {item}
                </Box>
              ))}
            </Box>
          </Box>

          {/* How to Exercise Rights */}
          <Box sx={{ mb: 6 }}>
            <Typography
              variant="h4"
              sx={{
                color: "primary.main",
                fontWeight: 700,
                fontSize: { xs: "1.5rem", md: "1.75rem" },
                mb: 4,
              }}
            >
              How to Exercise Your Rights
            </Typography>

            <Typography
              variant="body1"
              sx={{ color: "#555", lineHeight: 1.8, mb: 4 }}
            >
              To exercise your privacy rights or submit a request, you can:
            </Typography>

            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: { xs: "1fr", md: "repeat(2, 1fr)" },
                gap: 3,
              }}
            >
              <Box
                sx={{
                  p: 4,
                  borderRadius: "16px",
                  backgroundColor: "#f8f8f8",
                  textAlign: "center",
                }}
              >
                <Typography
                  variant="h6"
                  sx={{ color: "primary.main", fontWeight: 600, mb: 2 }}
                >
                  Email Us
                </Typography>
                <Typography variant="body1" sx={{ color: "#555" }}>
                  privacy@simplerhome.com
                </Typography>
              </Box>

              <Box
                sx={{
                  p: 4,
                  borderRadius: "16px",
                  backgroundColor: "#f8f8f8",
                  textAlign: "center",
                }}
              >
                <Typography
                  variant="h6"
                  sx={{ color: "primary.main", fontWeight: 600, mb: 2 }}
                >
                  Account Settings
                </Typography>
                <Typography variant="body1" sx={{ color: "#555" }}>
                  Manage preferences in your dashboard
                </Typography>
              </Box>
            </Box>
          </Box>

          {/* Verification */}
          <Box
            sx={{
              p: 4,
              borderRadius: "16px",
              backgroundColor: "#fff9e6",
              border: "1px solid #f5d76e",
            }}
          >
            <Typography
              variant="h6"
              sx={{ color: "#333", fontWeight: 600, mb: 2 }}
            >
              Verification Process
            </Typography>
            <Typography variant="body1" sx={{ color: "#555", lineHeight: 1.8 }}>
              To protect your privacy, we will verify your identity before
              processing any requests. This may include confirming your email
              address or other account information. We will respond to verified
              requests within 45 days.
            </Typography>
          </Box>

          {/* Submit Request Button */}
          <Box sx={{ textAlign: "center", mt: 8 }}>
            <Button
              variant="contained"
              color="secondary"
              size="large"
              onClick={() => navigate("/contact")}
              sx={{
                px: { xs: 5, md: 10, lg: 15 },
                py: { xs: 1.5, md: 2, lg: 2.5 },
                fontSize: { xs: "1rem", md: "1.1rem", lg: "1.2rem" },
                fontWeight: 500,
                borderRadius: "8px",
                textTransform: "none",
                color: "#ffffff",
              }}
            >
              Submit Privacy Request
            </Button>
          </Box>
        </Box>
      </FullPaddingWrapper>
    </Container>
  );
});

export default DoNotSell;
